import { createSelector, MemoizedSelector } from '@ngrx/store';
import moment from 'moment';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import {
    selectEffectiveStatusById,
    selectNotificationEndById,
    selectNotificationEntryById,
    selectNotificationStartById,
    selectOtherEntriesInProgress,
} from '../../notification/selectors/notification.selector';
import { NotificationState } from '../../notification/notification-collection.state';
import {
    selectCurrentStartOfWork,
    selectCurrentTimesheet,
    selectIsTimesheetFeatureEnabled,
    selectSubmittedEndOfWork,
} from '../../timesheet/selectors/timesheet.selector';
import {
    LastEditableTravelBackEntry,
    selectLastEditableTravelBackEntry,
} from '../../timesheet/selectors/timesheet-merge.selector';
import { selectIsPaceOnlineUser } from '../../user-master/selectors/user-master.selector';
import { NotificationIdState } from '../../utilities/notification-id.type';

/**
 * Everything `TravelStatusUpdateEffects.submitTravelStatus$` needs to run its pipeline, read in one
 * combined selector rather than reading each slice separately — this slice owns no feature state
 * of its own, so this file holds only cross-slice `createSelector` compositions.
 */
export type TravelStatusSubmitContext = Readonly<{
    currentStatusCode: NOTIFICATION_STATUS_CODE;
    isPaceOnlineUser: boolean;
    isTimesheetFeatureEnabled: boolean;
    startOfWork: moment.Moment;
    submittedEndOfWork: moment.Moment;
    currentTimesheetId: string | undefined;
    lastTravelBackEntry: LastEditableTravelBackEntry | undefined;
    otherInProgressIds: NotificationIdState[];
    notificationEntry: NotificationState | undefined;
    plannedStart: moment.Moment;
    plannedEnd: moment.Moment;
}>;

/**
 * @param notificationId the notification the travel status is being submitted for
 */
export const selectTravelStatusSubmitContext = (
    notificationId: NotificationIdState,
): MemoizedSelector<object, TravelStatusSubmitContext> =>
    createSelector(
        selectEffectiveStatusById(notificationId),
        selectIsPaceOnlineUser,
        selectIsTimesheetFeatureEnabled,
        selectCurrentStartOfWork,
        selectSubmittedEndOfWork,
        selectCurrentTimesheet,
        selectLastEditableTravelBackEntry,
        selectOtherEntriesInProgress(notificationId),
        selectNotificationEntryById(notificationId),
        selectNotificationStartById(notificationId),
        selectNotificationEndById(notificationId),
        (
            currentStatus,
            isPaceOnlineUser,
            isTimesheetFeatureEnabled,
            startOfWork,
            submittedEndOfWork,
            currentTimesheet,
            lastTravelBackEntry,
            otherInProgressIds,
            notificationEntry,
            plannedStart,
            plannedEnd,
        ): TravelStatusSubmitContext => ({
            currentStatusCode: currentStatus?.statusCode ?? NOTIFICATION_STATUS_CODE.DISP,
            isPaceOnlineUser,
            isTimesheetFeatureEnabled,
            startOfWork,
            submittedEndOfWork,
            currentTimesheetId: currentTimesheet?.timesheetId,
            lastTravelBackEntry,
            otherInProgressIds,
            notificationEntry,
            plannedStart: plannedStart ?? moment.invalid(),
            plannedEnd: plannedEnd ?? moment.invalid(),
        }),
    );
